import { useMemo, useState } from 'react';
import classNames from 'classnames';
import {
  DataTable,
  Dropdown,
  IconButton,
  Search,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  type DataTableRenderProps,
} from '@carbon/react';
import { Add, Location, ResetAlt, SubtractAlt } from '@carbon/react/icons';
import type { AreaGeometry } from 'types/area-geometry';
import { useReferenceSearch } from './hooks';
import type { EditorSession, ReferenceCandidate } from './types';
import {
  areaTypeLabel,
  geometryToPieces,
  isOutOfView,
  piecesBounds,
  referenceEmptyText,
  referenceModes,
  sortByDistance,
} from './utils';
import styles from './area-geometry-editor.module.scss';

interface ReferenceLayersSectionProps {
  area: AreaGeometry;
  session: EditorSession;
}

/** A table row: the cells Carbon sorts on, plus the action column. */
interface ReferenceTableRow {
  id: string;
  name: string;
  category: string;
  action: string;
}

const HEADERS = [
  { key: 'name', header: 'Area' },
  { key: 'category', header: 'Category' },
  { key: 'action', header: 'Actions' },
];

/**
 * The table's areas. With nothing typed: the areas in view ("Same type" scopes
 * them to the edited area's type), plus any on the map from beyond it, nearest
 * the edited area first and those out of view last. A search spans the whole
 * catalogue, on-map rows first.
 *
 * @param {AreaGeometry} area - The edited area.
 * @param {EditorSession} session - The reference state.
 * @param {string} text - What is typed in the search.
 * @return {ReferenceCandidate[]} The rows, in their default order.
 */
function useReferenceItems(
  area: AreaGeometry,
  session: EditorSession,
  text: string
): ReferenceCandidate[] {
  const { referenceAreas, displayedIds, areasInView, viewBounds } = session;
  const { candidates } = useReferenceSearch(text, area);
  const searching = text.trim() !== '';

  return useMemo<ReferenceCandidate[]>(() => {
    const shown = new Set(displayedIds);
    if (searching) {
      return [
        ...candidates.filter((item) => shown.has(item.id)),
        ...candidates.filter((item) => !shown.has(item.id)),
      ];
    }
    const inViewIds = new Set(areasInView.map((candidate) => candidate.id));
    const beyond: ReferenceCandidate[] = referenceAreas
      .filter((reference) => !inViewIds.has(reference.id))
      .map((reference) => ({
        id: reference.id,
        name: reference.name,
        category: reference.category,
        grossAcres: reference.grossAcres,
        extent: piecesBounds(geometryToPieces(reference.geometry)),
      }));
    const nearest = sortByDistance(
      [...areasInView, ...beyond],
      piecesBounds(geometryToPieces(area.geometry))
    );
    /**
     * Whether a row lies outside the view.
     *
     * @param {ReferenceCandidate} item - The row.
     * @return {boolean} True when out of view.
     */
    const outOfView = (item: ReferenceCandidate) =>
      isOutOfView(item, viewBounds, inViewIds);
    return [
      ...nearest.filter((item) => !outOfView(item)),
      ...nearest.filter(outOfView),
    ];
  }, [
    area.geometry,
    areasInView,
    candidates,
    displayedIds,
    referenceAreas,
    searching,
    viewBounds,
  ]);
}

interface ReferenceRowActionsProps {
  onMap: boolean;
  highlighted: boolean;
  onToggleMap: () => void;
  onToggleHighlight: () => void;
}

/**
 * A row's actions: Show on map, then add or remove it as a reference layer.
 *
 * @param {ReferenceRowActionsProps} props - The row's state and toggles.
 * @return {JSX.Element} The two buttons.
 */
function ReferenceRowActions({
  onMap,
  highlighted,
  onToggleMap,
  onToggleHighlight,
}: ReferenceRowActionsProps): JSX.Element {
  return (
    <div className={styles.areaTableActions}>
      <IconButton
        label={highlighted ? 'Clear map highlight' : 'Show on map'}
        kind="ghost"
        size="sm"
        align="left"
        isSelected={highlighted}
        onClick={onToggleHighlight}
      >
        <Location />
      </IconButton>
      <IconButton
        label={onMap ? 'Remove reference layer' : 'Add reference layer'}
        kind="ghost"
        size="sm"
        align="left"
        onClick={onToggleMap}
      >
        {onMap ? <SubtractAlt /> : <Add />}
      </IconButton>
    </div>
  );
}

interface ReferenceSearchBarProps {
  text: string;
  onTextChange: (text: string) => void;
  onReset: () => void;
}

/**
 * The search over the reference table, flush on its header, with Reset.
 *
 * @param {ReferenceSearchBarProps} props - The text and its handlers.
 * @return {JSX.Element} The bar.
 */
function ReferenceSearchBar({
  text,
  onTextChange,
  onReset,
}: ReferenceSearchBarProps): JSX.Element {
  return (
    <div className={styles.areaToolbar}>
      <Search
        id="edit-area-search"
        size="sm"
        labelText="Search areas"
        placeholder="Search all areas"
        value={text}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          onTextChange(event.target.value)
        }
        className={styles.areaSearch}
      />
      <IconButton
        label="Reset"
        kind="ghost"
        size="sm"
        align="bottom-end"
        onClick={onReset}
      >
        <ResetAlt />
      </IconButton>
    </div>
  );
}

/**
 * Reference Area Layers, as mock v10 lays it out: Filter areas, then a sortable
 * table under a search. With nothing typed it holds the scoped areas, nearest
 * first, with any on the map from out of view at the bottom; a search spans the
 * whole catalogue instead. Each row adds or removes the area as a
 * reference layer, and Show on map outlines and frames it.
 *
 * @param {ReferenceLayersSectionProps} props - The area and the session.
 * @return {JSX.Element} The section's content.
 */
export function ReferenceLayersSection({
  area,
  session,
}: ReferenceLayersSectionProps): JSX.Element {
  const {
    referenceMode,
    setReferenceMode,
    displayedIds,
    toggleOnMap,
    viewBounds,
    areasInView,
    highlightedIds,
    toggleHighlight,
  } = session;
  const [text, setText] = useState('');
  const modes = referenceModes(area.category);
  const searching = text.trim() !== '';

  const items = useReferenceItems(area, session, text);

  const byId = useMemo(
    () => new Map(items.map((item) => [String(item.id), item])),
    [items]
  );
  const rows = useMemo<ReferenceTableRow[]>(
    () =>
      items.map((item) => ({
        id: String(item.id),
        name: item.name,
        category: areaTypeLabel(item.category),
        action: '',
      })),
    [items]
  );

  const inViewIds = useMemo(
    () => new Set(areasInView.map((candidate) => candidate.id)),
    [areasInView]
  );

  return (
    <div className={styles.optionStack}>
      <Dropdown
        id="edit-show-areas-mode"
        size="sm"
        titleText="Filter areas"
        label="Filter areas"
        items={modes}
        itemToString={(item) => item?.label ?? ''}
        selectedItem={
          modes.find((mode) => mode.id === referenceMode) ?? modes[0]
        }
        onChange={({ selectedItem }) => {
          if (selectedItem) setReferenceMode(selectedItem.id);
        }}
      />

      <div className={styles.areaList} aria-label="Reference areas">
        <ReferenceSearchBar
          text={text}
          onTextChange={setText}
          onReset={() => {
            setText('');
            setReferenceMode('same-type');
          }}
        />
        <DataTable rows={rows} headers={HEADERS} isSortable size="sm">
          {({
            rows: sortedRows,
            headers,
            getTableProps,
            getHeaderProps,
            getRowProps,
          }: DataTableRenderProps<ReferenceTableRow, string[]>) => (
            <TableContainer className={styles.areaTable}>
              <Table
                {...getTableProps()}
                size="sm"
                aria-label={searching ? 'Results' : 'Scoped areas'}
              >
                <TableHead>
                  <TableRow>
                    {headers.map((header) =>
                      header.key === 'action' ? (
                        <TableHeader
                          key={header.key}
                          className={styles.areaTableActionCol}
                        >
                          {header.header}
                        </TableHeader>
                      ) : (
                        <TableHeader
                          {...getHeaderProps({ header })}
                          key={header.key}
                          className={
                            header.key === 'category'
                              ? styles.areaTableCategory
                              : undefined
                          }
                        >
                          {header.header}
                        </TableHeader>
                      )
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={headers.length}
                        className={styles.areaTableEmpty}
                      >
                        {referenceEmptyText(searching, referenceMode)}
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedRows.map((row) => {
                      const candidate = byId.get(row.id);
                      if (!candidate) return null;
                      const onMap = displayedIds.includes(candidate.id);
                      const highlighted = highlightedIds.includes(candidate.id);
                      return (
                        <TableRow
                          {...getRowProps({ row })}
                          key={row.id}
                          isSelected={onMap}
                          className={classNames({
                            [styles.areaTableRowOnMap]: onMap,
                            [styles.areaTableRowActive]: highlighted,
                          })}
                        >
                          <TableCell>
                            <div className={styles.areaTableName}>
                              <span
                                className={styles.areaListName}
                                title={candidate.name}
                              >
                                {candidate.name}
                              </span>
                              {isOutOfView(
                                candidate,
                                viewBounds,
                                inViewIds
                              ) && (
                                <span className={styles.areaListMeta}>
                                  Out of view
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell
                            className={styles.areaTableCategory}
                            title={areaTypeLabel(candidate.category)}
                          >
                            {areaTypeLabel(candidate.category)}
                          </TableCell>
                          <TableCell className={styles.areaTableActionCol}>
                            <ReferenceRowActions
                              onMap={onMap}
                              highlighted={highlighted}
                              onToggleMap={() =>
                                toggleOnMap(candidate.id, onMap)
                              }
                              onToggleHighlight={() =>
                                toggleHighlight(candidate)
                              }
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DataTable>
      </div>
    </div>
  );
}
